import { Button } from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import CustomModal, { ModalInput } from "./Modal";
import { autoConnectAndPrint, buildChallanEscPos } from "../utils/bluetoothService";
import { deleteOrder, updateOrder, createChallanApi } from "../utils/api";
import useStore from "../utils/store";

const ChallanTable = ({ data, refresh }) => {
  const user = useStore((state) => state.user);
  // Edit modal states (Grouped Challan CRUD)
  const [editOpen, setEditOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editWeight, setEditWeight] = useState("");
  const [editItems, setEditItems] = useState([]);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Open Edit Modal for Challan Group
  const handleOpenEdit = (item) => {
    const orders = item?.orders || [];
    setEditingGroup(item);

    // Find total existing weight
    let existingWeight = 0;
    for (const o of orders) {
      const w = parseFloat(o?.challanWeight || o?.weight || 0);
      if (w > 0) {
        existingWeight += w;
      }
    }
    setEditWeight(existingWeight > 0 ? String(parseFloat(existingWeight.toFixed(3))) : "");

    // Prepare items list for editing
    setEditItems(
      orders.map((o) => ({
        _id: o._id,
        productName: o?.product?.productName || o?.description || "Item",
        quantity: o?.quantity || "",
        description: o?.description || "",
      }))
    );
    setEditOpen(true);
  };

  const handleItemFieldChange = (idx, field, value) => {
    setEditItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  // CRUD: Handle Delete Challan Group
  const handleDeleteChallan = async (item) => {
    const orders = item?.orders || [];
    const ordersCount = orders.length;
    const challanId =
      item?.challanId || orders[0]?.challanId || "Challan";

    const confirmMsg =
      ordersCount > 1
        ? `⚠️ Are you sure you want to delete challan (${challanId}) and all its ${ordersCount} items?`
        : `⚠️ Are you sure you want to delete this challan item?`;

    if (window.confirm(confirmMsg)) {
      try {
        const deletePromises = orders.map((o) => deleteOrder(o._id));
        const results = await Promise.all(deletePromises);
        const allSuccess = results.every(([success]) => success);

        if (allSuccess) {
          toast.success("✅ Challan deleted successfully!");
        } else {
          toast.warn("Some items could not be deleted");
        }
        refresh?.();
      } catch (err) {
        console.error(err);
        toast.error("Error deleting challan: " + (err.message || err));
      }
    }
  };

  // CRUD: Handle Save Edit Challan Group
  const handleSaveEdit = async () => {
    if (!editingGroup || !editItems.length) return;

    for (const item of editItems) {
      if (
        !item.quantity ||
        isNaN(Number(item.quantity)) ||
        Number(item.quantity) <= 0
      ) {
        return toast.error(
          `Please enter a valid quantity for ${item.productName}`
        );
      }
    }

    setEditSubmitting(true);
    try {
      const numWeight = parseFloat(editWeight);
      const hasValidWeight = !isNaN(numWeight) && numWeight > 0;

      // Identify Tag category items
      const isTagItem = (it) => {
        const o = editingGroup?.orders?.find((ord) => String(ord._id) === String(it._id));
        const prodGroup = (o?.product?.group || "").toLowerCase().trim();
        const ordGroup = (o?.group || "").toLowerCase().trim();
        const prodName = (it.productName || "").toLowerCase().trim();
        return prodGroup === "tag" || ordGroup === "tag" || prodName.includes("tag");
      };

      const tagItems = editItems.filter(isTagItem);
      const totalTagQty = tagItems.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0);
      const totalAllQty = editItems.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0);

      const updatePromises = editItems.map((item) => {
        const isTag = isTagItem(item);
        const ordQty = parseFloat(item.quantity) || 0;
        let itemWeight = 0;

        if (hasValidWeight) {
          if (totalTagQty > 0) {
            if (isTag && ordQty > 0) {
              itemWeight = Number(((ordQty * numWeight) / totalTagQty).toFixed(3));
            } else {
              itemWeight = 0;
            }
          } else if (totalAllQty > 0) {
            if (ordQty > 0) {
              itemWeight = Number(((ordQty * numWeight) / totalAllQty).toFixed(3));
            } else {
              itemWeight = 0;
            }
          } else {
            itemWeight = Number((numWeight / editItems.length).toFixed(3));
          }
        }

        const payload = {
          quantity: Number(item.quantity),
          description: item.description || "",
          weight: itemWeight,
          challanWeight: itemWeight,
        };
        return updateOrder(item._id, payload);
      });

      const results = await Promise.all(updatePromises);
      const allSuccess = results.every(([success]) => success);

      if (allSuccess) {
        toast.success("✅ Challan updated successfully!");
        setEditOpen(false);
        refresh?.();
      } else {
        toast.warn("Some items could not be updated");
        refresh?.();
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating challan: " + (err.message || err));
    } finally {
      setEditSubmitting(false);
    }
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
      <div className="w-full overflow-x-auto overflow-y-auto max-h-[60vh]">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 z-20 shadow-xs">
            <tr className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 text-slate-900 border-b border-amber-500">
              <th className="px-3.5 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap bg-amber-400 sticky top-0">
                Challan ID
              </th>
              <th className="px-3.5 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap bg-amber-400 sticky top-0">
                Date
              </th>
              <th className="px-3.5 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap bg-amber-400 sticky top-0">
                Items
              </th>
              <th className="px-3.5 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap bg-amber-400 sticky top-0">
                Qnty
              </th>
              <th className="px-3.5 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap bg-amber-400 sticky top-0">
                Weight
              </th>
              <th className="px-3.5 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap bg-amber-400 sticky top-0">
                Print Challan
              </th>
              {user?.type !== "ViewAdmin" && (
                <th className="px-3.5 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider whitespace-nowrap bg-amber-400 sticky top-0">
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data?.map((item, index) => {
              const ordersCount = item?.orders?.length || 1;
              const firstOrder = item?.orders?.[0];
              const challanIdDisplay =
                item?.challanId ||
                firstOrder?.challanId ||
                `CH-${new Date(item?.challanDate || firstOrder?.challanDate || firstOrder?.updatedAt || Date.now()).toISOString().slice(2, 10).replace(/-/g, "")}-${String(index + 1).padStart(2, "0")}`;

              return (
                <React.Fragment key={index}>
                  {item?.orders?.map((order, index2) => {
                    const isFirst = index2 === 0;
                    const isGroupBottom = index2 === ordersCount - 1;
                    const ordWeight =
                      order?.challanWeight ||
                      order?.weight ||
                      "";

                    return (
                      <tr
                        key={order._id || index2}
                        className={`transition-colors hover:bg-slate-50/80 ${
                          isGroupBottom
                            ? "border-b-2 border-slate-300"
                            : "border-b border-slate-100"
                        }`}
                      >
                        {/* 1. Challan ID */}
                        {isFirst && (
                          <td
                            rowSpan={ordersCount}
                            className="px-3.5 py-2 text-xs font-bold text-center border-r border-slate-200 align-middle bg-slate-50/60"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-md font-mono font-bold text-xs whitespace-nowrap shadow-xs">
                                🔖 {challanIdDisplay}
                              </span>
                              {ordersCount > 1 && (
                                <span className="text-[10px] text-slate-500 font-semibold">
                                  ({ordersCount} items)
                                </span>
                              )}
                            </div>
                          </td>
                        )}

                        {/* 2. Date */}
                        <td className="px-3.5 py-2 text-xs text-gray-700 border-r border-slate-200 whitespace-nowrap">
                          <div className="font-semibold text-gray-800">
                            {new Date(
                              order?.challanDate || order?.updatedAt
                            ).toLocaleDateString()}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {new Date(
                              order?.challanDate || order?.updatedAt
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </td>

                        {/* 3. Items */}
                        <td className="px-3.5 py-2 text-xs font-bold text-gray-900 border-r border-slate-200">
                          <div>
                            {order?.product?.productName ||
                              order?.description ||
                              "-"}
                          </div>
                          {order?.description &&
                            order?.product?.productName && (
                              <div className="text-[11px] text-gray-500 font-normal">
                                {order?.description}
                              </div>
                            )}
                        </td>

                        {/* 4. Quantity */}
                        <td className="px-3.5 py-2 text-xs font-bold text-center border-r border-slate-200">
                          <span className="bg-blue-50 text-blue-800 border border-blue-200 px-2.5 py-0.5 rounded-md font-mono font-bold">
                            {order?.quantity}
                          </span>
                        </td>

                        {/* 5. Weight (Grouped per Challan: 1 challan = sum of all item weights) */}
                        {isFirst && (
                          <td
                            rowSpan={ordersCount}
                            className="px-3.5 py-2 text-xs font-bold text-center border-r border-slate-200 align-middle bg-white"
                          >
                            {(() => {
                              const groupWeight = item?.orders?.reduce((sum, o) => {
                                const w = parseFloat(o?.challanWeight || o?.weight || 0);
                                return sum + (isNaN(w) ? 0 : w);
                              }, 0);
                              return groupWeight && groupWeight > 0 ? (
                                <span className="bg-amber-100 text-amber-900 border border-amber-300 px-2.5 py-1 rounded-md font-mono font-bold inline-block text-xs">
                                  {parseFloat(groupWeight.toFixed(3))} kg
                                </span>
                              ) : (
                                <span className="text-gray-400 font-medium text-xs">
                                  -
                                </span>
                              );
                            })()}
                          </td>
                        )}

                        {/* 6. Print Challan (Grouped per dispatch when multiple, single when separate) */}
                        {isFirst && (
                          <td
                            rowSpan={ordersCount}
                            className="px-3.5 py-2 border-r border-slate-200 align-middle bg-white text-center"
                          >
                            <PrintChallanGroup
                              item={item}
                              orders={item?.orders || [order]}
                              refresh={refresh}
                            />
                          </td>
                        )}

                        {/* 7. Action (Grouped per Challan: 1 Edit & 1 Delete per Challan) */}
                        {isFirst && user?.type !== "ViewAdmin" && (
                          <td
                            rowSpan={ordersCount}
                            className="px-3.5 py-2 whitespace-nowrap align-middle bg-white text-center"
                          >
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                              {/* Edit Button */}
                              <button
                                onClick={() => handleOpenEdit(item)}
                                className="bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-bold px-2.5 py-1 rounded-md shadow-xs transition active:scale-95 cursor-pointer flex items-center gap-1 text-xs"
                                title="Edit Challan"
                              >
                                <span>✏️</span>
                                <span>Edit</span>
                              </button>

                              {/* Delete Button */}
                              <button
                                onClick={() => handleDeleteChallan(item)}
                                className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold px-2.5 py-1 rounded-md shadow-xs transition active:scale-95 cursor-pointer flex items-center gap-1 text-xs"
                                title="Delete Challan"
                              >
                                <span>🗑️</span>
                                <span>Delete</span>
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {(!data || data.length === 0) && (
              <tr>
                <td
                  colSpan={user?.type === "ViewAdmin" ? 6 : 7}
                  className="px-4 py-8 text-center text-gray-500 font-semibold bg-gray-50 text-sm"
                >
                  📋 No Pending Challans Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* CRUD: Edit Challan Modal */}
      {editOpen && (
        <CustomModal
          title={`Edit Challan - ${
            editingGroup?.challanId ||
            editingGroup?.orders?.[0]?.challanId ||
            "Challan"
          } (${editItems.length} ${
            editItems.length === 1 ? "Item" : "Items"
          })`}
          width="md:w-[42rem]"
          open={editOpen}
          setOpen={setEditOpen}
          button={
            <div className="flex justify-end gap-2 w-full pt-2">
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setEditOpen(false)}
                disabled={editSubmitting}
                style={{ textTransform: "none", fontWeight: "bold" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSaveEdit}
                disabled={editSubmitting}
                style={{ textTransform: "none", fontWeight: "bold" }}
              >
                {editSubmitting ? "Updating..." : "💾 Update Challan"}
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            {/* Total Challan Weight */}
            <div className="bg-white p-3 rounded-lg border border-slate-300 shadow-sm flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                <span>Total Challan Weight (kg):</span>
                {editWeight && parseFloat(editWeight) > 0 ? (
                  <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded">
                    {editWeight} kg
                  </span>
                ) : null}
              </label>
              <input
                type="number"
                step="any"
                min="0"
                value={editWeight}
                onChange={(e) => setEditWeight(e.target.value)}
                placeholder="Enter total weight in kg..."
                className="w-full border border-slate-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 p-2 rounded-md text-sm font-bold outline-none bg-slate-50"
              />
            </div>

            {/* Challan Items */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center px-1">
                <span className="text-xs font-bold text-slate-800">
                  Items in Challan ({editItems.length}):
                </span>
                <span className="text-[11px] text-slate-500 font-medium">
                  Edit Qty & Description per item
                </span>
              </div>

              <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
                {editItems.map((item, idx) => (
                  <div
                    key={item._id || idx}
                    className="bg-white p-3 rounded-lg border border-slate-200 shadow-xs flex flex-col gap-2"
                  >
                    <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
                      <span className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                        <span className="bg-slate-100 text-slate-700 w-5 h-5 rounded-full flex items-center justify-center text-[11px]">
                          {idx + 1}
                        </span>
                        <span>{item.productName}</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-slate-600">
                          Quantity:
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleItemFieldChange(
                              idx,
                              "quantity",
                              e.target.value
                            )
                          }
                          placeholder="Qty"
                          className="w-full border border-slate-300 focus:border-blue-500 p-1.5 rounded text-xs font-bold outline-none bg-slate-50"
                        />
                      </div>

                      <div className="flex flex-col gap-1 sm:col-span-2">
                        <label className="text-[11px] font-bold text-slate-600">
                          Remarks / Description:
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) =>
                            handleItemFieldChange(
                              idx,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder="Description / Note..."
                          className="w-full border border-slate-300 focus:border-blue-500 p-1.5 rounded text-xs outline-none bg-slate-50"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CustomModal>
      )}
    </div>
  );
};

// Challan Print Component (Handles both Single Order and Grouped Orders)
const PrintChallanGroup = ({ item, orders = [], refresh }) => {
  const [open, setOpen] = React.useState(false);
  const [weight, setWeight] = React.useState("");
  const [isPrinting, setIsPrinting] = React.useState(false);

  const firstOrder = orders[0];
  const customerName = firstOrder?.customer?.userName || "Customer";
  const group = firstOrder?.group || "";
  const isMultiple = orders.length > 1;
  const isTagItem = orders.some(
    (o) => (o?.product?.group || o?.group || "").toLowerCase().trim() === "tag"
  );

  // Calculate pre-filled weight if available (sum of weights in group)
  React.useEffect(() => {
    if (open) {
      let existingWeight = 0;
      for (const o of orders) {
        const w = parseFloat(o?.challanWeight || o?.weight || 0);
        if (w > 0) {
          existingWeight += w;
        }
      }
      if (existingWeight > 0) {
        setWeight(String(parseFloat(existingWeight.toFixed(3))));
      }
    }
  }, [open, orders, firstOrder]);

  const handleSaveOnly = async () => {
    const numWeight = parseFloat(weight);
    if (isTagItem && (!weight || isNaN(numWeight) || numWeight <= 0)) {
      return toast.error(
        "⚠️ Weight is REQUIRED for Tag category products! Please enter weight before saving."
      );
    }
    const finalWeight = !isNaN(numWeight) && numWeight > 0 ? numWeight : undefined;

    try {
      const targetIds = orders.map((o) => o._id);
      await createChallanApi(targetIds, finalWeight);
      toast.success("✅ Challan Saved Successfully!");
      setOpen(false);
      refresh?.();
      window.dispatchEvent(new Event("plabel-data-updated"));
      localStorage.setItem("plabel_last_sync", Date.now().toString());
    } catch (err) {
      console.error(err);
      toast.error("Error saving challan: " + (err.message || err));
    }
  };

  const handlePrint = async () => {
    const numWeight = parseFloat(weight);
    if (isTagItem && (!weight || isNaN(numWeight) || numWeight <= 0)) {
      return toast.error(
        "⚠️ Weight is REQUIRED for Tag category products! Please enter weight before printing."
      );
    }
    const finalWeight = !isNaN(numWeight) && numWeight > 0 ? numWeight : undefined;

    setIsPrinting(true);
    try {
      const targetIds = orders.map((o) => o._id);
      await createChallanApi(targetIds, finalWeight);
      const printData = buildChallanEscPos(orders, finalWeight, "challan");
      const dev = await autoConnectAndPrint(printData);
      toast.success(`✅ Printed to ${dev?.name || "Printer"}`);
      setOpen(false);
      refresh?.();
    } catch (err) {
      console.error(err);
      toast.error("Bluetooth error: " + (err.message || err));
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePdfClick = async () => {
    const numWeight = parseFloat(weight);
    if (isTagItem && (!weight || isNaN(numWeight) || numWeight <= 0)) {
      return toast.error(
        "⚠️ Weight is REQUIRED for Tag category products! Please enter weight before opening PDF."
      );
    }
    const finalWeight = !isNaN(numWeight) && numWeight > 0 ? numWeight : 0;
    try {
      const targetIds = orders.map((o) => o._id);
      await createChallanApi(targetIds, finalWeight > 0 ? finalWeight : undefined);
    } catch (err) {
      console.error(err);
    }
    setOpen(false);
    refresh?.();
    const challanId =
      item?.challanId ||
      firstOrder?.challanId ||
      item?._id?.phoneNumber ||
      firstOrder?.customer?.phoneNumber ||
      firstOrder?.customer?._id ||
      "challan";

    const orderParam = orders.map((o) => o._id).join(",");
    window.open(
      `/admin/dispatch/challan-pdf/${encodeURIComponent(challanId)}/${orderParam}/${finalWeight}`,
      "_blank"
    );
  };

  return (
    <div className="w-full">
      <button
        onClick={() => setOpen(true)}
        className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 cursor-pointer shadow-sm flex items-center justify-center gap-1 mx-auto"
      >
        <span>📄</span>
        <span>
          {isMultiple ? `Print Group Challan (${orders.length})` : "Print Challan"}
        </span>
      </button>

      {open && (
        <CustomModal
          title={
            isMultiple
              ? `Print Group Challan - ${customerName} (${orders.length} Items)`
              : `Print Challan - ${
                  firstOrder?.product?.productName || "Item"
                } (Qty: ${firstOrder?.quantity})`
          }
          open={open}
          setOpen={setOpen}
          button={
            <div className="grid grid-cols-3 gap-2 w-full pt-2">
              <Button
                variant="contained"
                onClick={handleSaveOnly}
                disabled={isPrinting}
                style={{
                  textTransform: "none",
                  fontWeight: "bold",
                  backgroundColor: "#2563eb",
                  color: "#fff",
                }}
              >
                💾 Save
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handlePrint}
                disabled={isPrinting}
                style={{ textTransform: "none", fontWeight: "bold" }}
              >
                {isPrinting ? "Printing..." : "⚡ BT Print (58mm)"}
              </Button>
              <Button
                variant="contained"
                onClick={handlePdfClick}
                disabled={isPrinting}
                style={{
                  textTransform: "none",
                  fontWeight: "bold",
                  backgroundColor: "#16a34a",
                  color: "#fff",
                }}
              >
                📄 PDF Print (3.5"x5")
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-3">
            {/* Customer & Items Summary */}
            <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
              <div className="font-bold text-xs text-gray-700 mb-1.5 flex justify-between items-center">
                <span>
                  Customer: <strong className="text-slate-900">{customerName} {group ? `(${group})` : ""}</strong>
                </span>
                {isTagItem ? (
                  <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-[11px] font-bold">
                    Tag Category (Weight Required)
                  </span>
                ) : (
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[11px] font-medium">
                    Non-Tag Category
                  </span>
                )}
              </div>

              {/* Items List */}
              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                {orders.map((ord, idx) => (
                  <div
                    key={ord._id || idx}
                    className="flex justify-between items-center text-xs bg-white px-2.5 py-1.5 rounded border border-gray-100"
                  >
                    <span className="font-semibold text-gray-800">
                      {ord?.product?.productName || ord?.description || "Item"}
                    </span>
                    <span className="font-bold text-blue-600 font-mono">
                      Qty: {ord?.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Weight Display (Non-Editable / Read-Only in Print Modal) */}
            {isTagItem && (
              <div className="flex flex-col gap-1 p-3 rounded-lg border-2 bg-yellow-50 border-amber-300">
                <label className="text-xs font-bold text-gray-800 flex items-center justify-between">
                  <span>
                    Total Weight (kg){" "}
                    <span className="text-slate-500 font-normal">(Saved Weight)</span>:
                  </span>
                  {weight && parseFloat(weight) > 0 ? (
                    <span className="text-[10px] text-green-700 bg-green-100 px-2 py-0.5 rounded font-bold flex items-center gap-1">
                      <span>🔒</span>
                      <span>{weight} kg</span>
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-500 bg-slate-200 px-2 py-0.5 rounded font-semibold">
                      0 kg
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={weight && parseFloat(weight) > 0 ? `${weight} kg` : "0 kg"}
                  className="w-full border border-slate-300 p-2 rounded text-sm font-bold outline-none bg-slate-100 text-slate-800 shadow-xs cursor-not-allowed select-none"
                />
              </div>
            )}
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default ChallanTable;
